import type { ApiStrapiProduct } from '../services/strapi/product/types';
import {
  createProject,
  projectMetrics,
  cableLength,
  type CameraKind,
  type ProjectDraft,
  type EquipmentSlot,
} from './project.ts';
import { PROJECT_PRICING, roundMoney } from './projectPricing.ts';

// Compatibility rules contain no prices. Prices and availability always come from Strapi.
export const cameraModels: Record<CameraKind, string[]> = {
  indoor: ['vigi-c240i-28', 'dahua-hdw1431t1p-s4-28'],
  outdoor: ['vigi-c340i-28', 'vigi-c340i-4', 'dahua-hfw1431t-zs-s4', 'hikvision-1347g2-luf-4'],
};
// Conservative planning envelopes, not measured server performance guarantees.
const switches = [
  { slug: 'tp-link-ls106lp-v2', ports: 4, watts: 41, uplink: 100 },
  { slug: 'tp-link-ls109p', ports: 8, watts: 63, uplink: 100 },
  { slug: 'tp-link-sg1005p-v2', ports: 4, watts: 65, uplink: 1000 },
  { slug: 'tp-link-sg1210mp', ports: 8, watts: 123, uplink: 1000 },
];
const archiveModels = [
  'wd-purple-wd11purz-1tb',
  'wd-purple-wd23purz-2tb',
  'wd-purple-wd44purz-4tb',
  'seagate-skyhawk-st8000vx010-8tb',
  'wd-purple-wd10purz-1tb',
  'wd-purple-wd30purz-3tb',
  'wd-purple-wd40purz-4tb',
];
const servers = [
  { slug: 'raspberry-pi-4-4gb-kit', cameras: 4, mbps: 16, disks: 2, directOnly: true },
  { slug: 'beelink-mini-s12-pro-16-500', cameras: 16, mbps: 96, disks: 4, directOnly: false },
];
export interface EquipmentOption {
  slug: string;
  title: string;
  quantity: number;
  total: number;
}
export interface EquipmentChoice {
  key: string;
  label: string;
  selected: string;
  options: EquipmentOption[];
  cameraIds?: string[];
}
export interface EquipmentLine {
  product: ApiStrapiProduct;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
  detail: string;
  choiceKey?: string;
  cameraIds?: string[];
}
export interface ProjectQuote {
  lines: EquipmentLine[];
  missing: string[];
  choices: EquipmentChoice[];
  notes: string[];
  subtotal: number;
  equipmentTotal: number | null;
  installationLines: InstallationLine[];
  installationTotal: number;
  total: number | null;
  storageTB: number;
  poePorts: number;
}
export interface InstallationLine {
  title: string;
  quantity: number;
  unit: string;
  unitPrice: number;
  total: number;
}
export function storageCapacity(product: ApiStrapiProduct) {
  if (product.equipment_type !== 'storage') return 0;
  const feature = product.key_features?.find((item) => item.label === 'Объём');
  const match = feature?.value.match(/^\s*(\d+(?:[.,]\d+)?)\s*(ТБ|ГБ|TB|GB)\s*$/i);
  return match ? Number(match[1].replace(',', '.')) / (/^(ГБ|GB)$/i.test(match[2]) ? 1000 : 1) : 0;
}
export function installationEstimate(draft: ProjectDraft) {
  const lines: InstallationLine[] = [];
  if (!draft.installation || !draft.cameras.length) return { lines, total: 0 };
  const metrics = projectMetrics(draft),
    rates = PROJECT_PRICING.installation;
  const add = (title: string, quantity: number, unit: string, unitPrice: number) => {
    if (quantity)
      lines.push({ title, quantity, unit, unitPrice, total: roundMoney(quantity * unitPrice) });
  };
  add(
    'Установка камер в помещении',
    draft.cameras.filter((c) => c.kind === 'indoor').length,
    'шт.',
    rates.indoorCamera,
  );
  add(
    'Установка уличных камер',
    draft.cameras.filter((c) => c.kind === 'outdoor').length,
    'шт.',
    rates.outdoorCamera,
  );
  // Installation uses route metres, excluding spare cable that is not laid.
  add('Прокладка и подключение кабеля', metrics.cableRoute, 'м', rates.cableMetre);
  add(
    draft.includeArchive === false
      ? 'Настройка Robotics Vision и сети'
      : 'Настройка Robotics Vision, сети и архива',
    1,
    'компл.',
    rates.systemSetup,
  );
  return { lines, total: roundMoney(lines.reduce((sum, line) => sum + line.total, 0)) };
}
export function hasCatalogPrice(product: ApiStrapiProduct) {
  const price = Number(product.price);
  return product.in_stock && !product.price_on_request && Number.isFinite(price) && price > 0;
}
export function availableCameras(products: ApiStrapiProduct[], kind: CameraKind) {
  return cameraModels[kind]
    .map((slug) =>
      products.find((product) => product.slug === slug && product.equipment_type === 'camera'),
    )
    .filter((product): product is ApiStrapiProduct => !!product && hasCatalogPrice(product));
}
export function projectEquipment(draft: ProjectDraft, products: ApiStrapiProduct[]): ProjectQuote {
  const metrics = projectMetrics(draft);
  const lines: EquipmentLine[] = [],
    missing: string[] = [],
    notes: string[] = [];
  const choices: EquipmentChoice[] = [];
  const overrides = draft.equipmentOverrides || {};
  const archiveProducts = products.filter(
    (p) => archiveModels.includes(p.slug || '') && hasCatalogPrice(p) && storageCapacity(p) > 0,
  );
  const largestDisk = Math.max(0, ...archiveProducts.map(storageCapacity));
  const totalMbps = metrics.count * draft.bitrate;
  const longestRun = Math.max(0, ...draft.cameras.map((c) => cableLength(c, draft)));
  let storageTB = 0,
    poePorts = 0;
  const find = (slug: string) => products.find((p) => p.slug === slug && hasCatalogPrice(p));
  type Candidate = {
    product: ApiStrapiProduct;
    quantity: number;
    detail: string;
    unit?: string;
    price?: number;
  };
  const cost = (c: Candidate) => roundMoney(c.quantity * (c.price ?? Number(c.product.price)));
  function add(c: Candidate | undefined, label: string, choiceKey?: string) {
    if (!c) {
      missing.push(label);
      return;
    }
    lines.push({
      ...c,
      unit: c.unit || 'шт.',
      unitPrice: c.price ?? Number(c.product.price),
      total: cost(c),
      choiceKey,
    });
  }
  function choose(
    slot: EquipmentSlot,
    label: string,
    candidates: Candidate[],
    automatic?: Candidate,
  ) {
    const sorted = [...candidates].sort((a, b) => cost(a) - cost(b));
    const selected = overrides[slot] || '';
    choices.push({
      key: slot,
      label,
      selected,
      options: sorted.map((c) => ({
        slug: c.product.slug!,
        title: c.product.title,
        quantity: c.quantity,
        total: cost(c),
      })),
    });
    const chosen = selected
      ? sorted.find((c) => c.product.slug === selected)
      : automatic || sorted[0];
    add(
      chosen,
      selected
        ? `${label}: выбранная модель недоступна или не подходит; замените её или включите автоподбор`
        : label,
      slot,
    );
    return chosen;
  }
  const fixed = (slugs: string[], quantity: number, detail: string): Candidate[] =>
    slugs.flatMap((slug) => {
      const product = find(slug);
      return product ? [{ product, quantity, detail }] : [];
    });
  if (metrics.count) {
    for (const camera of draft.cameras) {
      const options = availableCameras(products, camera.kind);
      const product = camera.productSlug
        ? options.find((p) => p.slug === camera.productSlug)
        : options[0];
      if (!product) {
        const key = `camera:${camera.id}`;
        choices.push({
          key,
          label: `Камера «${camera.name}»`,
          selected: camera.productSlug || '',
          cameraIds: [camera.id],
          options: options.map((p) => ({
            slug: p.slug!,
            title: p.title,
            quantity: 1,
            total: Number(p.price),
          })),
        });
        missing.push(
          `Камера «${camera.name}»${camera.productSlug ? ` (${camera.productSlug})` : ''}`,
        );
        continue;
      }
      const key = `camera:${camera.kind}:${product.slug}:${camera.productSlug ? 'manual' : 'auto'}`;
      const existing = lines.find((l) => l.choiceKey === key);
      if (existing) {
        existing.quantity++;
        existing.total = roundMoney(existing.unitPrice * existing.quantity);
        existing.cameraIds!.push(camera.id);
      } else {
        add(
          {
            product,
            quantity: 1,
            detail: camera.kind === 'indoor' ? 'Для помещений · PoE' : 'Для улицы · PoE',
          },
          'Камера',
          key,
        );
        lines[lines.length - 1].cameraIds = [camera.id];
      }
    }
    for (const line of lines.filter((l) => l.cameraIds)) {
      const camera = draft.cameras.find((c) => c.id === line.cameraIds![0])!;
      choices.push({
        key: line.choiceKey!,
        label: camera.kind === 'indoor' ? 'Камеры в помещении' : 'Уличные камеры',
        selected: camera.productSlug || '',
        cameraIds: line.cameraIds,
        options: availableCameras(products, camera.kind).map((p) => ({
          slug: p.slug!,
          title: p.title,
          quantity: line.quantity,
          total: roundMoney(Number(p.price) * line.quantity),
        })),
      });
    }
    // VIGI fixed IR cameras: 5 W DC in datasheet; allow 8 W at PoE input.
    // Other approved models conservatively reserve a complete 802.3af port.
    const cameraWatts = draft.cameras.map((c) => {
      const slug = c.productSlug || availableCameras(products, c.kind)[0]?.slug || '';
      return slug.startsWith('vigi-') ? 8 : 15.4;
    });
    const portWatts = Math.max(...cameraWatts) + (metrics.longRuns ? 3 : 0);
    const network = switches.flatMap((spec) => {
      const product = find(spec.slug);
      if (!product || (metrics.longRuns && spec.uplink < 1000)) return [];
      // Reserve 20% power and 25% bandwidth; all ports fit even with uneven allocation.
      const usablePorts = Math.min(spec.ports, Math.floor(spec.watts / (portWatts * 1.2)));
      if (!usablePorts || (metrics.count > 8 && spec.ports < 8)) return [];
      const quantity = Math.ceil(metrics.count / usablePorts);
      if (quantity > 1 && ['tp-link-ls109p', 'tp-link-sg1005p-v2'].includes(spec.slug)) return [];
      if (totalMbps * 1.25 > spec.uplink) return [];
      return [
        {
          product,
          quantity,
          detail: `${spec.ports} PoE-порта · ${spec.watts} Вт · uplink ${spec.uplink} Мбит/с · до ${usablePorts} выбранных камер на коммутатор`,
        },
      ];
    });
    // One enclosure/switch is easier to install; within that topology take the cheapest model.
    network.sort((a, b) => a.quantity - b.quantity || cost(a) - cost(b));
    const selectedSwitch = choose('switch', 'PoE-коммутатор', network, network[0]);
    if (selectedSwitch)
      poePorts =
        switches.find((s) => s.slug === selectedSwitch.product.slug)!.ports *
        selectedSwitch.quantity;
    if (metrics.longRuns) {
      const feasible = longestRun <= 180;
      if (!feasible)
        missing.push(
          'Трасса длиннее 180 м: нужна отдельная схема с оптикой или промежуточным узлом',
        );
      else
        choose(
          'extender',
          'PoE-повторитель',
          fixed(
            ['tp-link-poe10e'],
            metrics.longRuns,
            'Для линии 90–180 м: установить в защищённом месте так, чтобы каждый сегмент был не длиннее 90 м',
          ),
        );
      notes.push(
        `Самая длинная трасса — ${Math.ceil(longestRun)} м. ${feasible ? 'Добавлены PoE-повторители и коммутатор с запасом питания. Места установки и питание у камеры проверим на объекте.' : 'Стоимость дополнительной сетевой инфраструктуры требует расчёта.'}`,
      );
    }
    const serverCandidates = servers
      .filter(
        (s) =>
          metrics.count <= s.cameras &&
          totalMbps <= s.mbps &&
          !(s.directOnly && draft.needsTranscoding) &&
          (!largestDisk || metrics.storageTB <= largestDisk * s.disks),
      )
      .flatMap((s) =>
        fixed(
          [s.slug],
          1,
          s.directOnly
            ? 'Комплект: плата 4 ГБ, блок питания, корпус с охлаждением, microSD для ОС. До 4 камер и 16 Мбит/с суммарно; H.264 без перекодирования'
            : `Мини-ПК с запасом ресурсов · ${metrics.count} камер, суммарный поток ${totalMbps} Мбит/с`,
        ),
      );
    const server = choose('server', 'Сервер Robotics Vision', serverCandidates);
    const serverSpec = servers.find((s) => s.slug === server?.product.slug);
    const maxDisks = serverSpec?.disks || 2;
    if (serverSpec?.directOnly)
      notes.push(
        'Raspberry Pi — предварительный подбор для просмотра и записи исходного потока H.264. Нагрузку и воспроизведение на ваших устройствах проверяем перед установкой. Если нужно преобразование видео, включите режим преобразования видео.',
      );
    else if (server)
      notes.push(
        'Мини-ПК выбран с учётом числа камер, суммарного потока и режима обработки. Производительность при перекодировании проверяется отдельно.',
      );
    if (draft.includeArchive !== false) {
      const candidates = archiveProducts.flatMap((product) => {
        const capacity = storageCapacity(product),
          quantity = Math.max(1, Math.ceil(metrics.storageTB / capacity));
        if (quantity > maxDisks) return [];
        return [
          {
            product,
            quantity,
            detail: `${capacity} ТБ на диск · всего ${capacity * quantity} ТБ`,
          },
        ];
      });
      // Compare the complete archive, including a powered enclosure per disk.
      const enclosurePrice = Number(find('orico-3588us3-v1')?.price || 0);
      candidates.sort(
        (a, b) =>
          cost(a) + a.quantity * enclosurePrice - cost(b) - b.quantity * enclosurePrice ||
          a.quantity - b.quantity ||
          storageCapacity(a.product) - storageCapacity(b.product),
      );
      const disk = choose('storage', 'Диск для архива', candidates, candidates[0]);
      if (disk) {
        storageTB = storageCapacity(disk.product) * disk.quantity;
        choose(
          'enclosure',
          'Корпус для подключения диска',
          fixed(['orico-3588us3-v1'], disk.quantity, 'Отдельное питание для каждого HDD 3,5″'),
        );
      }
    }
    for (const kind of ['indoor', 'outdoor'] as const) {
      const length = metrics.cableByKind[kind];
      if (!length) continue;
      // Cable sheath suitability is preserved when replacing equipment.
      const slugs = kind === 'indoor' ? ['rexant-01-0043-cu'] : ['rexant-01-0045-cu'];
      const candidates = slugs.flatMap((slug) => {
        const product = find(slug);
        const feature = product?.key_features?.find((item) => item.label === 'Бухта');
        const coilLength = Number(
          feature?.value.match(/^\s*(\d+(?:[.,]\d+)?)\s*м\s*$/i)?.[1]?.replace(',', '.'),
        );
        return product && coilLength > 0
          ? [
              {
                product,
                quantity: length,
                unit: 'м',
                price: roundMoney(Number(product.price) / coilLength),
                detail: `Цена метра из бухты ${coilLength} м`,
              },
            ]
          : [];
      });
      choose(
        `cable-${kind}`,
        kind === 'indoor' ? 'Кабель для помещений' : 'Уличный кабель',
        candidates,
      );
    }
    if (draft.includeRouter)
      choose(
        'router',
        'Роутер',
        fixed(
          ['tp-link-er605-v2', 'keenetic-hopper-kn3810', 'tp-link-archer-mr600-v3'],
          1,
          'Подключение системы к сети; функции Wi-Fi и мобильной связи зависят от выбранной модели',
        ),
      );
    if (draft.includeUps)
      choose(
        'ups',
        'Источник бесперебойного питания',
        fixed(['ippon-back-basic-850-euro'], 1, 'Резервное питание'),
      );
  }
  const subtotal = roundMoney(lines.reduce((sum, line) => sum + line.total, 0));
  const installation = installationEstimate(draft);
  const equipmentTotal = missing.length ? null : subtotal;
  return {
    lines,
    missing: [...new Set(missing)],
    choices,
    notes,
    subtotal,
    equipmentTotal,
    installationLines: installation.lines,
    installationTotal: installation.total,
    total: equipmentTotal === null ? null : roundMoney(equipmentTotal + installation.total),
    storageTB,
    poePorts,
  };
}

export function createTypicalProject(
  object: 'business' | 'home' | 'warehouse',
  count: 4 | 8,
): ProjectDraft {
  const draft = createProject();
  draft.name = `Типовой проект на ${count} камер`;
  draft.object = object === 'business' ? 'shop' : object;
  draft.width = object === 'warehouse' ? 40 : object === 'home' ? 12 : 20;
  draft.height = object === 'warehouse' ? 25 : object === 'home' ? 8 : 14;
  const template = draft.cameras;
  draft.cameras = Array.from({ length: count }, (_, i) => ({
    ...template[i % 4],
    id: `cam-${i + 1}`,
    name:
      count === 4 && object === 'home'
        ? ['Вход', 'Прихожая', 'Комната', 'Двор'][i % 4]
        : `Камера ${i + 1}`,
    kind:
      count === 8
        ? 'indoor'
        : object === 'home'
          ? i % 4 === 0 || i % 4 === 3
            ? 'outdoor'
            : 'indoor'
          : object === 'warehouse' && i % 4 < 2
            ? 'outdoor'
            : template[i % 4].kind,
    x: count === 8 ? 0.12 + (i % 4) * 0.25 : template[i % 4].x,
    y: count === 8 ? (i < 4 ? 0.18 : 0.82) : template[i % 4].y,
    angle: count === 8 ? (i < 4 ? 90 : 270) : template[i % 4].angle,
  }));
  return draft;
}
